import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Briefcase, ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import usePageMeta from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: "Careers · Adviserz — Build market intelligence infrastructure",
    description: "Open roles at Adviserz, a brand of Quant Core Technologies Private Limited — quantitative research, machine learning engineering, data and design.",
    canonical: "https://adviserz.in/careers",
  });

  useEffect(() => {
    axios.get(`${API}/jobs`).then(r => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background text-white">
      <section className="relative bg-[#08090B] border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 spotlight-column" />
        <SiteHeader active="careers" />
        <div className="relative max-w-4xl mx-auto px-6 pt-40 pb-24 text-center">
          <div className="overline mb-6 justify-center">Careers · Adviserz</div>
          <h1 className="font-display font-medium text-4xl sm:text-6xl tracking-tighter leading-[1.04]">
            Join us in building <br />
            <span className="brand-text-gradient">market intelligence infrastructure.</span>
          </h1>
          <p className="mt-8 text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            We're a small, technical team building quantitative analytics and market intelligence technology. If you obsess over data, statistics and clean execution — let's talk.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-24">
        <div className="label-xs mb-6 text-center">Open Roles · {jobs.length}</div>
        {loading ? (
          <div className="text-muted-foreground text-sm">Loading roles…</div>
        ) : jobs.length === 0 ? (
          <div className="card-flat p-10 text-center">
            <div className="font-display font-bold text-xl">No open roles right now.</div>
            <p className="mt-2 text-sm text-muted-foreground">We're still hiring opportunistically — write to <a className="text-white hover:underline" href="mailto:info@adviserz.in">info@adviserz.in</a>.</p>
          </div>
        ) : (
          <ul className="grid gap-3">
            {jobs.map((j) => (
              <li key={j.id} data-testid={`job-${j.id}`}
                className="card-flat p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 hover:bg-accent/40 transition-colors">
                <div className="flex-1 min-w-0">
                  <Link to={`/careers/${j.slug}`} className="font-display font-bold text-xl hover:text-ai transition-colors">
                    {j.title}
                  </Link>
                  <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {j.department}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location}</span>
                    <span className="px-2 py-0.5 rounded-sm border border-border">{j.employment_type}</span>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{j.description}</p>
                </div>
                <Link to={`/careers/${j.slug}`} data-testid={`apply-${j.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-background font-semibold hover:bg-white/90 transition-colors shrink-0">
                  View &amp; Apply <ArrowRight className="w-4 h-4" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <SiteFooter />
    </div>
  );
}
